import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { invalidateAfterMutation } from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { notificationEndpoints } from "@/features/notification/api/endpoints";
import type {
  SimulateEventResponse,
  SimulateWalletTransactionCompletedRequest,
} from "@/features/notification/api/dto";

/**
 * Déclenche la notification d'une transaction aboutie
 * (`POST /NotificationEvents/simulate-wallet-transaction-completed` —
 * guide §6.7). Diffusion en éventail (client + éventuellement agent pour
 * sa commission), déduplication par `eventId`.
 * Rôles : flashpay-superadmin, flashpay-service.
 */
export const useSimulateTransactionCompleted = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [
      "notification",
      "events",
      "simulate-wallet-transaction-completed",
    ],
    mutationFn: (body: SimulateWalletTransactionCompletedRequest) =>
      mutateData({
        url: notificationEndpoints.events.simulateWalletTransactionCompleted,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<SimulateEventResponse>,
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        [["NOTIFICATION_LOGS_LIST"]],
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSimulateTransactionCompleted failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "notification" }
      );
    },
  });
};
